import React from 'react';
import {
  Box,
  Button,
  FormControl,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import type { NutritionalAnalysisMealPlanNameItem } from '@/types/meal-plan';
import { analysisTabs, colors } from './analysis.shared';

const selectChevronProps = {
  IconComponent: KeyboardArrowDownOutlinedIcon,
} as const;

const outlinedFieldSx = {
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#4a5565',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1.333px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1.333px',
  },
  '& .MuiSelect-icon': {
    color: '#4a5565',
    fontSize: 14,
    right: 10,
  },
} as const;

export interface AnalysisHeaderControlsProps {
  mealPlans: NutritionalAnalysisMealPlanNameItem[];
  selectedMealPlanId: string;
  onMealPlanChange: (mealPlanId: string) => void;
  mealPlansLoading?: boolean;
  subtitleLine: string;
  /** 0 = By the Day, 1 = By the plan */
  viewMode: number;
  onViewModeChange: (next: number) => void;
  selectedDayIndex: number;
  analysisDayCount: number;
  dayLabels?: string[];
  onSelectedDayIndexChange: (next: number) => void;
  activeAnalysisTab: number;
  onAnalysisTabChange: (next: number) => void;
  backButtonLabel?: string;
  onBackClick?: () => void;
  /** Plan-level GET /meal-plans/:id/nutritional-analysis in flight */
  nutritionalAnalysisLoading?: boolean;
  /** Shown on the same row as the analysis tabs, right-aligned (optional). */
  tabBarEndAdornment?: React.ReactNode;
}

export const AnalysisHeaderControls: React.FC<AnalysisHeaderControlsProps> = ({
  mealPlans,
  selectedMealPlanId,
  onMealPlanChange,
  mealPlansLoading = false,
  subtitleLine,
  viewMode,
  onViewModeChange,
  selectedDayIndex,
  analysisDayCount,
  dayLabels = [],
  onSelectedDayIndexChange,
  activeAnalysisTab,
  onAnalysisTabChange,
  backButtonLabel = 'Back to Meal Plan',
  onBackClick,
  nutritionalAnalysisLoading = false,
  tabBarEndAdornment,
}) => {
  const handleDayChange = (event: SelectChangeEvent<number>) => {
    const v = event.target.value;
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) onSelectedDayIndexChange(n);
  };

  const handleMealPlanChange = (event: SelectChangeEvent<string>) => {
    onMealPlanChange(event.target.value);
  };

  const mealPlanIds = React.useMemo(() => new Set(mealPlans.map((p) => p.id)), [mealPlans]);
  const mealPlanSelectValue =
    selectedMealPlanId && mealPlanIds.has(selectedMealPlanId) ? selectedMealPlanId : '';

  const mealPlanPlaceholder = mealPlansLoading ? 'Loading…' : 'Meal Plan Name';

  const dayOptionLabels = React.useMemo(
    () =>
      dayLabels.length > 0
        ? dayLabels
        : Array.from({ length: Math.max(1, analysisDayCount) }, (_, i) => `Day ${i + 1}`),
    [analysisDayCount, dayLabels],
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '30px',
          minWidth: 0,
        }}
      >
        {/* Row 1: meal plan selector + By the Day / By the plan */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 2 },
            width: '100%',
          }}
        >
          <FormControl
            size="small"
            sx={{
              maxWidth: { xs: '100%', md: 401 },
              width: { xs: '100%', md: 401 },
              flexShrink: 0,
            }}
          >
            <Select<string>
              value={mealPlanSelectValue}
              displayEmpty
              disabled={mealPlansLoading || mealPlans.length === 0}
              onChange={handleMealPlanChange}
              renderValue={(value) => {
                const id = String(value ?? '');
                if (!id || !mealPlanIds.has(id)) return mealPlanPlaceholder;
                return mealPlans.find((p) => p.id === id)?.name ?? id;
              }}
              {...selectChevronProps}
              sx={{
                ...outlinedFieldSx,
                height: 45,
                pl: '11px',
                pr: '11px',
                boxSizing: 'border-box',
              }}
            >
              {mealPlans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Figma 97:1496 — segmented By the Day / By the plan (slate track + white pill) */}
          <Box
            sx={{
              alignSelf: { xs: 'stretch', sm: 'auto' },
              flexShrink: 0,
              display: { xs: 'flex', sm: 'inline-flex' },
              width: { xs: '100%', sm: 'auto' },
              alignItems: 'flex-start',
              bgcolor: '#f1f5f9',
              borderRadius: '7.43px',
              p: '6.19px',
              boxSizing: 'border-box',
            }}
          >
            {(
              [
                { mode: 0 as const, label: 'By the Day' },
                { mode: 1 as const, label: 'By the plan' },
              ] as const
            ).map(({ mode, label }) => {
              const selected = viewMode === mode;
              return (
                <Button
                  key={mode}
                  type="button"
                  disableRipple
                  onClick={() => onViewModeChange(mode)}
                  sx={{
                    flex: { xs: 1, sm: '0 0 auto' },
                    minWidth: 0,
                    px: '14.857px',
                    py: '7.429px',
                    borderRadius: '3px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '17.33px',
                    lineHeight: '24.76px',
                    letterSpacing: 0,
                    whiteSpace: 'nowrap',
                    color: selected ? '#0f172a' : '#334155',
                    bgcolor: selected ? '#fff' : 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                    '&:hover': {
                      bgcolor: selected ? '#fff' : 'rgba(255, 255, 255, 0.45)',
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Row 2: title + subtitle | day */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, md: 2 },
            minHeight: 44,
            width: '100%',
          }}
        >
          <Box sx={{ minWidth: 0, flex: { md: '1 1 auto' } }}>
            {onBackClick ? (
              <Button
                type="button"
                variant="text"
                onClick={onBackClick}
                startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  mb: 0.5,
                  minWidth: 0,
                  minHeight: 28,
                  px: 0,
                  py: 0,
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: '18px',
                  color: '#6A7282',
                  '& .MuiButton-startIcon': {
                    mr: '4px',
                  },
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#1e2939',
                  },
                }}
              >
                {backButtonLabel}
              </Button>
            ) : null}
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '28px',
                letterSpacing: '-0.4395px',
                color: '#1e2939',
              }}
            >
              Detailed Analysis
            </Typography>
            <Typography
              component="p"
              sx={{
                mt: '1px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#99a1af',
                maxWidth: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {subtitleLine}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            {viewMode === 0 ? (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={Math.min(selectedDayIndex, Math.max(0, dayOptionLabels.length - 1))}
                  onChange={handleDayChange}
                  {...selectChevronProps}
                  sx={{
                    ...outlinedFieldSx,
                    height: 38,
                    pl: '11px',
                    pr: '11px',
                    bgcolor: '#fff',
                    boxSizing: 'border-box',
                  }}
                >
                  {dayOptionLabels.map((label, i) => (
                    <MenuItem key={i} value={i}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
        </Box>
      </Box>

      {nutritionalAnalysisLoading ? (
        <LinearProgress
          sx={{
            mt: 0.5,
            height: 2,
            borderRadius: 999,
            bgcolor: '#e5e7eb',
            '& .MuiLinearProgress-bar': { bgcolor: colors.cyan },
          }}
        />
      ) : null}

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 1 },
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Tabs
          value={activeAnalysisTab}
          onChange={(_, value: number) => onAnalysisTabChange(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons="auto"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 44,
            borderBottom: 'none',
            '& .MuiTabs-flexContainer': {
              gap: { xs: 1, sm: 1.5 },
            },
            '& .MuiTabs-scrollButtons': {
              minHeight: 44,
              '&.Mui-disabled': { opacity: 0.35 },
            },
            '& .MuiTab-root': {
              minHeight: 44,
              px: { xs: 0.5, sm: 0.75 },
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.78rem', sm: '0.82rem' },
              fontWeight: 500,
              color: '#6A7282',
              whiteSpace: 'nowrap',
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#0f8bc7',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 999,
              bgcolor: colors.cyan,
            },
          }}
        >
          {analysisTabs.map((tabLabel) => (
            <Tab key={tabLabel} label={tabLabel} />
          ))}
        </Tabs>
        {tabBarEndAdornment ? (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              pb: { xs: 0.5, sm: '5px' },
            }}
          >
            {tabBarEndAdornment}
          </Box>
        ) : null}
      </Box>
    </>
  );
};
